/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        UI State Management Store (Zustand)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

interface UiState {
  sidebarExpanded: boolean;
  mobileDrawerOpen: boolean;
  activeModal: string | null;
  modalPayload: unknown | null;
  commandPaletteOpen: boolean;

  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;
  setMobileDrawerOpen: (open: boolean) => void;
  openModal: (modalId: string, payload?: unknown) => void;
  closeModal: () => void;
  setCommandPaletteOpen: (open: boolean) => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
}

const SIDEBAR_STORAGE_KEY = 'sophia_sidebar_expanded';

const getInitialSidebarState = (): boolean => {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
  return stored !== null ? stored === 'true' : true;
};

export const useUiStore = create<UiState>((set) => ({
  sidebarExpanded: getInitialSidebarState(),
  mobileDrawerOpen: false,
  activeModal: null,
  modalPayload: null,
  commandPaletteOpen: false,

  toggleSidebar: () => {
    set((state) => {
      const next = !state.sidebarExpanded;
      if (typeof window !== 'undefined') {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      }
      return { sidebarExpanded: next };
    });
  },

  setSidebarExpanded: (expanded: boolean) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(expanded));
    }
    set({ sidebarExpanded: expanded });
  },

  setMobileDrawerOpen: (open: boolean) => set({ mobileDrawerOpen: open }),

  openModal: (modalId: string, payload: unknown = null) =>
    set({ activeModal: modalId, modalPayload: payload }),

  closeModal: () => set({ activeModal: null, modalPayload: null }),

  setCommandPaletteOpen: (open: boolean) => set({ commandPaletteOpen: open }),

  openCommandPalette: () => set({ commandPaletteOpen: true }),

  closeCommandPalette: () => set({ commandPaletteOpen: false }),

  toggleCommandPalette: () =>
    set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),
}));
